import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FormTextInput, FormLocationSelector } from "components/form-fields";
import { LinkButton } from "components/buttons";
import { Filter, FilterButton } from "components/filtering";
import { ListingTable } from "components/listing";
import { useDebounce } from "hooks/useDebounce";
import { useDispatch, useSelector } from "react-redux";
import { EmployeeService, GreyFleetService, LicenceCheckingService } from "services";
import { useQuery } from "hooks/useQuery";
import { NotificationInline, NotificationToaster } from "components/notifications";
import { Intent } from "@blueprintjs/core";
import { EmployeeStatus, StatusBadge } from "components/status";
import axios from "axios";
import { SetFiltering } from "state/actions";
import { Dropdown } from "components/dropdown";
import { InstantReportButton } from "components/reporting";

export function SearchGreyFleetStats() {
    //General
    const filterName = "greyFleetSearchStats";
    const [isLoadingSearchRequest, setIsLoadingSearchRequest] = useState(true);
    const [isLoadingOptions, setIsLoadingOptions] = useState(true);
    //Query
    const query = useQuery();
    const [selectedStatus, setSelectedStatus] = useState(query.get("filter"));
    //Search
    const debounceTimeout = 750;
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);
    //Options
    const [employeeTypeOptions, setEmployeeTypeOptions] = useState([]);
    const [employeeStatusOptions, setEmployeeStatusOptions] = useState([]);
    const [licenceCheckingOptions, setLicenceCheckingOptions] = useState(null);
    //Filters
    const [showFilters, setShowFilters] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [pageFilters, setPageFilters] = useState([]);
    const [defaultFilterValues, setDefaultFilterValues] = useState([]);
    // Reports
    const [showReportList, setShowReportList] = useState(false);
    const [reportList, setReportList] = useState([]);
    const [reportGenerationId, setReportGenerationId] = useState(null);
    //Location
    const dispatch = useDispatch();
    const reduxLocations = useSelector(
        (state) => state.licenceChecking.locations
    );
    const [useHierarchy, setUseHierarchy] = useState(true);
    const [useLocationDefaults, setUseLocationDefaults] = useState(null);
    // Listing Table
    const tableHeaders = [
        "Employee",
        "Location",
        "Registration",
        "Vehicle record",
        "Insurance",
        "Licence Check",
        "Policy",
        "MOT",
        "Tax",
        "Check Status",
        "View Check",
    ];
    const sortableHeaders = [
        { headerName: "Employee", sortName: "FULLNAME" },
        { headerName: "Location", sortName: "LOCATIONNAME" },
        { headerName: "Registration", sortName: "REGISTRATION" },
        { headerName: "Insurance", sortName: "INSURANCETASKSTATUS" },
        {
            headerName: "Licence Check",
            sortName: "LICENCECHECKTASKSTATUS",
        },
        { headerName: "Policy", sortName: "POLICYTASKSTATUS" },
        { headerName: "Tax", sortName: "TAXTASKSTATUS" },
        { headerName: "MOT", sortName: "MOTTASKSTATUS" },
    ];
    const [totalRecords, setTotalRecords] = useState(0);
    const [tableRows, setTableRows] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [sortedBy, setSortedBy] = useState(sortableHeaders[0]);
    const [sortedDir, setSortedDir] = useState("A");
    const [completedChecks, setCompletedChecks] = useState({});
    //Refs for tracking previous values
    const currentPageNumber = useRef();
    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentAppliedFilters = useRef();
    const currentLocations = useRef();
    const currentSearchTerm = useRef();
    const currentStatus = useRef();

    const statusFilterMap = {
        INVALIDTAX: {
            key: "FAILUREREASON",
            displayName: "Failure Reason",
            value: "TAXFAILURE",
        },
        INVALIDMOT: {
            key: "FAILUREREASON",
            displayName: "Failure Reason",
            value: "MOTFAILURE",
        },
        INCOMPLETE: {
            key: "POLICYSTATUS",
            displayName: "Policy Status",
            value: "AWAITINGRESPONSE",
        },
        APPROVALREQUIRED: {
            key: "CHECKSTATUS",
            displayName: "Awaiting Approval",
            value: "AWAITINGAPPROVAL",
        },
        REFUSEDDECLARATION: {
            key: "FAILUREREASON",
            displayName: "Failure Reason",
            value: "LICENCEDECLARATIONREFUSAL",
        },
        NOGOVDRIVERRECORD: {
            key: "FAILUREREASON",
            displayName: "Failure Reason",
            value: "LICENCENORECORD",
        },
        INVALIDINSURANCE: {
            key: "FAILUREREASON",
            displayName: "Failure Reason",
            value: "INSURANCEFAILURE",
        },
        EXPIREDINSURANCE: {
            key: "FAILUREREASON",
            displayName: "Failure Reason",
            value: "INSURANCEEXPIRED",
        },
        CONFLICTINGDETAILS: {
            key: "FAILUREREASON",
            displayName: "Failure Reason",
            value: "LICENCECONFLICTINGDETAILS",
        },
        AWAITINGINSURANCEDOCS: {
            key: "INSURANCESTATUS",
            displayName: "Insurance Status",
            value: "AWAITINGRESPONSE",
        },
        AWAITINGINSURANCEVERFICATION: {
            key: "INSURANCESTATUS",
            displayName: "Insurance Status",
            value: "INPROGRESS",
        },
        AWAITINGDECLARATION: {
            key: "LICENCECHECKSTATUS",
            displayName: "Licence Check Status",
            value: "AWAITINGRESPONSE",
        },
        FAILEDVEHICLEPOLICY: {
            key: "FAILUREREASON",
            displayName: "Failure Reason",
            value: "POLICYFAILURE",
        },
        FAILEDDRIVERPOLICY: {
            key: "FAILUREREASON",
            displayName: "Failure Reason",
            value: "LICENCEPOINTLIMIT",
        },
        NOGOVVEHICLERECORD: {
            key: "FAILUREREASON",
            displayName: "Failure Reason",
            value: "VEHICLENOTFOUND",
        },
        DRIVERDISQUALIFIED: {
            key: "FAILUREREASON",
            displayName: "Failure Reason",
            value: "LICENCEDISQUALIFIED",
        },
        VALIDCHECKS: {
            key: "CHECKSTATUS",
            displayName: "Check Status",
            value: "VALID",
        },
        INVALIDCHECKS: {
            key: "CHECKSTATUS",
            displayName: "Check Status",
            value: "INVALID",
        },
        REJECTEDCHECKS: {
            key: "CHECKSTATUS",
            displayName: "Check Status",
            value: "REJECTED",
        },
    };

    useEffect(() => {
        axios
            .all([
                EmployeeService.getEmployeeOptions(),
                LicenceCheckingService.getLicenceCheckingOptions(),
            ])
            .then(function ([employeeOptions, licenceOptions]) {
                setEmployeeStatusOptions(employeeOptions.employeeStatuses);
                setEmployeeTypeOptions(employeeOptions.employeeTypes);
                setLicenceCheckingOptions(licenceOptions);

                setPageFilters([
                    {
                        name: "EMPLOYEESTATUS",
                        displayName: "Employee Status",
                        items: employeeOptions.employeeStatuses.filter(
                            (status) => {
                                return status.active;
                            }
                        ),
                    },
                    {
                        name: "EMPLOYEETYPE",
                        displayName: "Employee Type",
                        items: employeeOptions.employeeTypes,
                    },
                    {
                        name: "EMPLOYEECATEGORY",
                        displayName: "Employee Category",
                        items: employeeOptions.employeeCategories,
                    },
                    {
                        name: "FAILUREREASON",
                        displayName: "Failure Reason",
                        items: licenceOptions.failureReasons.map((reason) => {
                            return { id: reason.key, name: reason.name };
                        }),
                    },
                    {
                        name: "MOTSTATUS",
                        displayName: "Mot Status",
                        items: licenceOptions.taskStatuses.map((status) => {
                            return { id: status.key, name: status.name };
                        }),
                    },
                    {
                        name: "TAXSTATUS",
                        displayName: "Tax Status",
                        items: licenceOptions.taskStatuses.map((status) => {
                            return { id: status.key, name: status.name };
                        }),
                    },
                    {
                        name: "POLICYSTATUS",
                        displayName: "Policy Status",
                        items: licenceOptions.taskStatuses.map((status) => {
                            return { id: status.key, name: status.name };
                        }),
                    },

                    {
                        name: "LICENCECHECKSTATUS",
                        displayName: "Licence Check Status",
                        items: licenceOptions.taskStatuses.map((status) => {
                            return { id: status.key, name: status.name };
                        }),
                    },
                    {
                        name: "INSURANCESTATUS",
                        displayName: "Insurance Status",
                        items: licenceOptions.taskStatuses.map((status) => {
                            return { id: status.key, name: status.name };
                        }),
                    },
                    {
                        name: "CHECKSTATUS",
                        displayName: "Check Status",
                        items: [
                            {
                                id: "AWAITINGAPPROVAL",
                                name: "Awaiting Approval",
                            },
                            { id: "INPROGRESS", name: "In Progress" },
                            { id: "REJECTED", name: "Rejected" },
                            { id: "VALID", name: "Valid" },
                            { id: "INVALID", name: "Invalid" },
                        ],
                    },
                ]);

                if (selectedStatus === "NONE") {
                    dispatch(SetFiltering(filterName, []));
                    setAppliedFilters([]);
                    setDefaultFilterValues([]);

                    setIsLoadingOptions(false);
                    return;
                }

                const filter = statusFilterMap[selectedStatus];
                if (!filter) {
                    setIsLoadingOptions(false);
                    return;
                }

                setAppliedFilters([
                    {
                        name: filter.key,
                        displayName: filter.displayName,
                        value: filter.value,
                    },
                ]);

                setDefaultFilterValues([
                    {
                        name: filter.key,
                        displayName: filter.displayName,
                        value: filter.value,
                    },
                ]);

                setIsLoadingOptions(false);
            });
    }, []);

    useEffect(() => {
        fetchTableData(false);
    }, [
        pageNumber,
        sortedBy,
        sortedDir,
        debouncedSearchTerm,
        appliedFilters,
        isLoadingOptions,
    ]);

    useEffect(() => {
        if (reduxLocations && reduxLocations.length > 0) {
            setUseLocationDefaults(false);
        } else {
            setUseLocationDefaults(true);
        }
    }, [reduxLocations]);

    useEffect(() => {
        if (
            !completedChecks ||
            !licenceCheckingOptions ||
            licenceCheckingOptions.failureReasons.length === 0
        ) {
            setTableRows([]);
            setTotalRecords(0);
            return;
        }
        setTableRows(
            completedChecks.data.map((vo) => {
                return [
                    <div
                        key={`${vo.employee.id}-status`}
                        className="inline-items nowrap"
                    >
                        <EmployeeStatus
                            statusId={vo.employee.statusId}
                            typeId={vo.employee.typeId}
                            typeAttributeMappings={employeeTypeOptions}
                            statusAttributeMappings={employeeStatusOptions}
                        />
                        <Link
                            to={`/employee/${vo.employee.id}/licence-checking`}
                        >
                            {vo.employee.name}
                        </Link>
                    </div>,
                    vo.employee.location,
                    vo.registration,
                    renderStatus(vo.vehicleRecordStatus, {
                        "incomplete record": "text-intent-danger",
                        complete: "",
                    }),
                    renderStatusForInsurance(
                        vo.checkStatus,
                        vo.insuranceTaskFailureReason,
                        vo.insuranceTaskStatus,
                        vo.insuranceResult
                    ),
                    renderStatusWithFailureReason(
                        vo.licenceCheckFailureReason,
                        vo.licenceCheckStatus,
                        vo.licenceCheckResult
                    ),
                    renderStatusWithFailureReason(
                        vo.policyFailureReason,
                        vo.policyStatus,
                        vo.policyResult
                    ),
                    renderStatusWithFailureReason(
                        vo.motFailureReason,
                        vo.motStatus,
                        vo.motResult
                    ),
                    renderStatusWithFailureReason(
                        vo.taxFailureReason,
                        vo.taxStatus,
                        vo.taxResult
                    ),
                    renderStatus(vo.checkStatus, {
                        valid: "text-intent-success",
                        invalid: "text-intent-danger",
                        rejected: "text-intent-danger",
                    }),
                    <LinkButton
                        key={`${vo.employee.id}-view-check`}
                        href={`/licence-checking/grey-fleet/check/${vo.checkId}`}
                        text="View"
                        intent="primary"
                        large={false}
                    />,
                ];
            })
        );

        setTotalRecords(completedChecks.totalCount);
    }, [completedChecks]);

    function renderStatus(value, mapping) {
        const statusClass = mapping[value?.toLowerCase()];
        return statusClass ? (
            <span className={statusClass}>{value}</span>
        ) : (
            <span className="text-intent-info nowrap">{value}</span>
        );
    }

    function renderStatusForInsurance(
        checkStatus,
        failureReasonValue,
        taskStatus,
        taskResult
    ) {
        if (checkStatus.toLowerCase() === "rejected") {
            return <span className="text-intent-danger">Rejected</span>;
        } else {
            return renderStatusWithFailureReason(
                failureReasonValue,
                taskStatus,
                taskResult
            );
        }
    }

    function renderStatusWithFailureReason(
        failureReasonValue,
        taskStatus,
        taskResult
    ) {
        if (!taskResult && failureReasonValue) {
            let failureReason = licenceCheckingOptions?.failureReasons.find(
                (reason) => reason.name === failureReasonValue
            );
            if (failureReason) {
                return (
                    <StatusBadge
                        text={
                            taskStatus.toLowerCase() !== "completed"
                                ? taskStatus
                                : "Invalid"
                        }
                        iconName="error"
                        intent={
                            failureReason.hardFail
                                ? Intent.DANGER
                                : Intent.WARNING
                        }
                        showTooltip
                        tooltipContent={failureReasonValue}
                        tooltipPosition="top"
                    />
                );
            } else {
                return failureReasonValue;
            }
        } else if (taskStatus.toLowerCase() === "awaiting response") {
            return (
                <span className="text-intent-info nowrap">
                    Awaiting Employee
                </span>
            );
        } else if (taskStatus.toLowerCase() !== "completed") {
            return (
                <span className="text-intent-info nowrap">{taskStatus}</span>
            );
        } else if (taskResult) {
            return "Valid";
        } else {
            return "Calculating";
        }
    }

    function fetchTableData(forceRefresh) {
        if (!reduxLocations) {
            setTableRows([]);
            setTotalRecords(0);
            setIsLoadingOptions(false);
            return;
        }

        if (isLoadingOptions) {
            return;
        }

        if (
            currentPageNumber.current === pageNumber &&
            currentTableSortBy.current === sortedBy &&
            currentTableSortDir.current === sortedDir &&
            currentSearchTerm.current === debouncedSearchTerm &&
            currentAppliedFilters.current === appliedFilters &&
            currentLocations.current === reduxLocations &&
            currentStatus.current === selectedStatus &&
            !forceRefresh
        ) {
            //If none of the values have changed, then 2 have been updated at once, so prevent the listing from performing another call.
            return;
        }

        currentPageNumber.current = pageNumber;
        currentTableSortBy.current = sortedBy;
        currentTableSortDir.current = sortedDir;
        currentSearchTerm.current = debouncedSearchTerm;
        currentAppliedFilters.current = appliedFilters;
        currentLocations.current = reduxLocations;
        currentStatus.current = selectedStatus;

        setIsLoadingSearchRequest(true);

        const mappedFilters = mapFilters();

        const pageSize = 50;

        const searchRequest = {
            requestCount: pageSize,
            pageNumber: pageNumber,
            sortField: sortedBy.sortName,
            sortDirection: sortedDir,
            filters: mappedFilters,
            searchTerm: debouncedSearchTerm,
        };

        LicenceCheckingService.searchGreyFleetChecks(searchRequest)
            .then(
                (response) => {
                    setCompletedChecks(response);
                },
                (error) => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Could not retrieve onboarding records, please try again"
                    );
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Could not retrieve onboarding records, please try again"
                );
            })
            .finally(() => {
                setIsLoadingSearchRequest(false);
            });
    }

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
    }

    function onPagingChange(newPageNumber) {
        setPageNumber(newPageNumber);
    }

    function onFilterChange(listingFilters) {
        if (listingFilters.length === 0) {
            setSelectedStatus(null);
            setDefaultFilterValues(listingFilters);
        }
        setAppliedFilters(listingFilters);
    }

    function onToggleFilter() {
        setShowFilters((prevState) => {
            return !prevState;
        });
    }

    function onTableSort(header, direction) {
        setSortedDir(direction);
        setSortedBy(header);
    }

    function onSelectedLocationsChange(locations) {
        dispatch({
            type: "SET_LICENCE_CHECKING_LOCATIONS",
            payload: locations,
        });
    }

    function mapFilters() {
        let mappedFilters = (currentAppliedFilters.current ?? []).map(function (
            f
        ) {
            return {
                key: f.name,
                value: f.value,
            };
        });

        if (currentLocations.current != null) {
            const mappedLocations = currentLocations.current.map(function (l) {
                return { key: "Location", value: l };
            });

            mappedFilters = [...mappedFilters, ...mappedLocations];
        }

        return mappedFilters;
    }

    function onReportDropdownClick() {
        setShowReportList((prev) => !prev);
    }

    useEffect(
        function () {
            setReportList([
                <InstantReportButton
                    buttonType="MenuItem"
                    icon={<i className="fa fa-file-pdf-o"></i>}
                    key={"grey-fleet-checks-report"}
                    text={"Grey Fleet Checks"}
                    onReportGenerate={generateReport}
                    onReportDownloadComplete={generateCreditReportComplete}
                    reportId={reportGenerationId}
                />,
            ]);

            setTimeout(() => {
                setShowReportList(false);
            });
        },
        [reportGenerationId]
    );

    function generateReport() {
        GreyFleetService.generateChecksReport(mapFilters(), currentSearchTerm.current)
            .then(
                (response) => {
                    setReportGenerationId(response);
                },
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Failed to generate report. Please try again."
                    );
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Failed to generate report. Please try again."
                );
            });
    }

    function generateCreditReportComplete() {
        setReportGenerationId(null);
    }

    return (
        <>
            <div className="filter-controls">            
                <div className="inline-items spacer-bottom-small">
                    <div className="inline-item fill">
                        <div className="form-field-full">
                            <FormTextInput
                                placeholder="Search Employees"
                                onChange={onSearchChange}
                                value={searchTerm}
                                large
                                disabled={isLoadingSearchRequest}
                                icon="search"
                                id="onboarding-search-field"
                            />
                        </div>
                    </div>
                    <div className="inline-item">
                        <FormLocationSelector
                            businessArea={"Tacho"}
                            loading={isLoadingSearchRequest}
                            selectedLocations={reduxLocations}
                            setSelectedLocations={onSelectedLocationsChange}
                            onLocationListClose={fetchTableData}
                            useLocationDefaults={useLocationDefaults}
                            useHierarchy={useHierarchy}
                            setUseHierarchy={setUseHierarchy}
                            locationDefaultsToTrue={
                                isLoadingSearchRequest ? null : true
                            }
                            disabled={isLoadingSearchRequest}
                        />
                    </div>
                    <div className="inline-item">
                        <Dropdown
                            isOpen={showReportList}
                            onClick={onReportDropdownClick}
                            intent="primary"
                            text={""}
                            icon={"document"}
                            large
                            iconOnly
                            id="reports"
                            items={reportList}
                            preamble={
                                <div>
                                    <h4>Downloads</h4>
                                    <p>
                                        Filters set on this page will be applied to
                                        the report when it is generated. Please make
                                        sure the data on the screen represents what
                                        you want to see within your report.
                                    </p>
                                </div>
                            }
                            disabled={false}
                        />
                    </div>
                    <div className="inline-item">
                        <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                    </div>                    
                </div>
                <Filter
                    filterName={filterName}
                    visible={showFilters}
                    filters={pageFilters}
                    onUpdate={onFilterChange}
                    defaultFilterValues={defaultFilterValues}
                    onClickOutside={onToggleFilter}
                />
            </div>
            <NotificationInline
                intent="info"
                text="Your report has been queued, and will download as soon as it's ready."
                show={reportGenerationId != null}
            />
            <ListingTable
                showPerPage={false}
                pageSize={50}
                headers={tableHeaders}
                data={tableRows}
                totalRecordCount={totalRecords}
                loadingData={isLoadingSearchRequest}
                onPagingChange={onPagingChange}
                sortable
                sortableHeaders={sortableHeaders}
                sortedBy={sortedBy}
                sortedDir={sortedDir}
                onSort={onTableSort}
            />
        </>
    );
}
